import { AbstractComponent } from '@/AbstractComponent'
import { useDate } from '@/composables/useDate.ts'
import { useStrings } from '@/composables/useStrings.ts'
import { useCubeStore } from '@/stores/useCubeStore'
import { useHistoryStore } from '@/stores/useHistoryStore'
import { useMapStore } from '@/stores/useMapStore'
import { useSettingsStore } from '@/stores/useSettingsStore'
import { Component } from 'vue-facing-decorator'

import ChartControls from '@/components/ChartControls.vue'
import DayChart from '@/components/history/DayChart.vue'
import DayPriceChart from '@/components/history/DayPriceChart.vue'
import MonthChart from '@/components/history/MonthChart.vue'
import PowerStatistics from '@/components/history/PowerStatistics.vue'
import IconSpinner from '@/components/icons/Spinner.vue'

import type { TradingStatisticsRequestParams } from '@/types/TradingStatisticsRequestParams'
import type { Moment } from 'moment'

import { usePlans } from '@/composables/usePlans'
import { useAccountStore } from '@/stores/useAccountStore'
import type { HistoryDataState } from '@/types/HistoryData'
import { DateTimeUtil } from '@/util/DateTimeUtil'
import moment from 'moment'
import { computed, watch, type ComputedRef } from 'vue'

type Granularity = 'day' | '15min' | '1day'
type TimeSpan = 'day' | 'month'

const { moment: momentHelper } = useDate()
const { t } = useStrings()

@Component({
  components: {
    IconSpinner,
    PowerStatistics,
    ChartControls,
    DayChart,
    DayPriceChart,
    MonthChart
  }
})
export default class PageHistory extends AbstractComponent {
  public EARLIEST_MONTH_DATE = new Date('2021-08-01')

  private historyStore = useHistoryStore()
  private cubeStore = useCubeStore()
  private plans = usePlans()

  private settingsStore = useSettingsStore()
  private mapStore = useMapStore()
  private dateHelpers = useDate()
  private accountStore = useAccountStore()

  public isLoaded = false
  public timespan: TimeSpan = 'day'
  public moment = momentHelper
  public t = t

  private reloadTimeout: number | undefined

  public _chartData: HistoryDataState | null = null

  public get chartDate(): Date {
    return this.historyStore.historySelectedDate
  }

  public get isFlexPlan(): boolean {
    return this.historyStore.isFlexPlan
  }

  public get isFloatPlan(): boolean {
    return this.historyStore.isFloatPlan
  }

  public get isBusinessPlan(): boolean {
    return this.historyStore.isBusinessPlan
  }

  public get selectedDateInFuture(): boolean {
    return this.historyStore.historySelectedDate > new Date()
  }

  public get granularity(): Granularity {
    if (this.timespan === 'day') {
      return '15min'
    }

    return '1day'
  }

  public get overlayClasses(): string {
    const result: string[] = ['chart__overlay']

    if (!this.isLoaded) {
      result.push('chart__overlay--visible')
    }

    return result.join(' ')
  }

  public get noEarlierMonthAvailable(): boolean {
    return (
      this.chartDate.getTime() <= this.EARLIEST_MONTH_DATE.getTime() && this.timespan === 'month'
    )
  }

  public get noFlexYet(): boolean {
    const todayAt5pm = momentHelper()

    todayAt5pm.hour(17).minute(0).second(0)

    return (
      this.chartDate.getTime() > new Date().getTime() &&
      todayAt5pm.toDate().getTime() > new Date().getTime() &&
      this.timespan === 'day'
    )
  }

  public get pricesUpdating(): boolean {
    return this.mapStore.producerPricesUpdating
  }

  public get showPriceChart(): boolean {
    const isTimespanValid = this.timespan === 'day'

    if (this.historyStore.isFlexPlan) {
      const planFrom = DateTimeUtil.getOnlyDate(this.cubeStore.planFrom);
      const planTo = DateTimeUtil.getOnlyDate(this.cubeStore.planTo);

      return (
        isTimespanValid &&
        (!planFrom || this.chartDate.getTime() >= planFrom?.getTime()) &&
        (!planTo || this.chartDate.getTime() <= planTo?.getTime())
      )
    }

    if (this.historyStore.isFloatPlan) {
      return (isTimespanValid && this.chartDate.getTime() >= moment().startOf('month').toDate().getTime())
    }

    if (!this.historyStore.isFlexPlan && !this.historyStore.isBusinessPlan) {
      return (isTimespanValid && this.chartDate.getTime() >= moment().startOf('month').toDate().getTime())
    }

    const hasFixPrice = this.plans.isFixPlan() &&
      (
        (this.hasProduction && this.plans.hasGenerationPlanStandardPrice()) ||
        this.hasConsumption
      )

    return isTimespanValid && hasFixPrice
  }

  public get isChartToday(): boolean {
    return this.dateHelpers.moment(this.historyStore.historySelectedDate).isSame(new Date(), 'day')
  }

  public onCalendarSelected(date: Date, isToday = false): void {
    const selectedDate = moment(date).startOf('day')

    this.historyStore.historySelectedDate = selectedDate.toDate()
    this.loadHistoryData(selectedDate, this.timespan, this.granularity)
  }

  public onTimespanChanged(time: TimeSpan): void {
    this.timespan = time
  }

  private async loadHistoryData(chartDate: Moment = moment(this.chartDate), timeSpan: TimeSpan = this.timespan, granularity: Granularity = this.granularity): Promise<void> {
    this.isLoaded = false

    const params: TradingStatisticsRequestParams = {
      granularity: granularity,
      startDate: chartDate,
      timespan: timeSpan
    }

    await this.historyStore.loadHistoryData(params)

    this.chartData = JSON.parse(JSON.stringify(this.historyStore.chartData))
    this.isLoaded = true
  }

  public set chartData(chartData: HistoryDataState) {
    this._chartData = chartData
  }

  public get chartData(): HistoryDataState | null {
    return this._chartData
  }

  public get hasConsumption(): boolean {
    return this.cubeStore.hasConsumption;
  }

  public get hasProduction(): boolean {
    return this.cubeStore.hasProduction;
  }

  public get showCommunitylessHint(): boolean {
    return this.isCommunityLess;
  }

  public get isCommunityLess(): boolean {
    return this.accountStore.properties.isCommunityless;
  }

  public override async mounted(): Promise<void> {
    const consumptionOrGenerationPlan: ComputedRef<string> = computed(
      () => this.settingsStore.contractData.plans.con?.name || this.settingsStore.contractData.plans.gen?.name
    )
    const producerPriceKeys = computed(() => Object.keys(this.mapStore.globalProducerPrices))

    watch(
      [consumptionOrGenerationPlan, producerPriceKeys],
      async ([consumptionOrGenerationPlan, producerPriceKeys]) => {
        if (consumptionOrGenerationPlan && producerPriceKeys.length > 0) {
          this.loadHistoryData()
          this.reloadTimeout = setInterval(() => this.loadHistoryData(), 15 * 60 * 1000)
        }
      },
      { immediate: true }
    )
  }

  public override async beforeUnmount(): Promise<void> {
    if (this.reloadTimeout) {
      clearInterval(this.reloadTimeout)
    }
  }
}
